import buttonStyles from '@styles/scss/buttons/_buttons.module.scss';
import styles from '@styles/scss/performer/_performerVideos.module.scss';
import addScript from 'lib/functions/videoScript';
import { Performer } from 'lib/types/item/performer';
import { VideoGallery } from 'lib/types/item/videoGallery';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import LightBox from './ligthBox';

export default function PerformerVideos({
    performer,
    gender,
    videos,
    sendData,
    videoGallery,
    currentLang,
    blurStatus
}: {
    performer: Performer;
    gender: string;
    videos: any;
    sendData: any;
    videoGallery: VideoGallery;
    currentLang: string;
    blurStatus: string;
}): JSX.Element {
    const src = `${performer.card_image}`;
    const index = 0;
    const length = videos.length;
    const [blur, setBlur] = useState('');
    const [current, setCurrent] = useState(0);
    const [width, setWidth] = useState(0);
    const [lightBox, setLightBox] = useState(false);
    const [threeVid, setThreeVid] = useState(false);
    let scrolled = false;

    const getLightBox = function (val: boolean) {
        setLightBox(val);
    };

    const getBlur = function (val: string) {
        sendData(val);
        setBlur('');
    };

    useEffect(() => {
        window.addEventListener('scroll', () => {
            if (scrolled === true) {
                return null;
            } else if (scrolled === false && window.scrollY > 50 && window.scrollY < 400) {
                /* This is to help performance on mobile, on initial load of mobile page the videos don't display. 
              Not untill the user begins to scroll will they be added */
                scrolled = true;
                addScript(videos);
            }
        });
    }, []);

    useEffect(() => {
        setWidth(window.innerWidth);
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    if (width >= 1200) {
        /* always load videos on desktop */
        scrolled = true;
        addScript(videos);
    }

    let recordedVideoAmount = 0;
    let thumbnailPath = '';
    if (performer.videos) {
        recordedVideoAmount = 1;
        const perfomerId = performer.videos[0].path.split('/')[1];
        const filename = performer.videos[0].path.split('/')[2].split('.')[0] + '.jpg';
        thumbnailPath = `models/${perfomerId}/${filename}`;
    }

    const nextSlide = () => {
        if (lightBox && recordedVideoAmount === 1) {
            setCurrent(current === length + recordedVideoAmount - 1 ? 0 : current + 1);
            addScript(videos);
        } else if (lightBox || (width >= 450 && length % 2 == 0)) {
            /* controls for if the lightbox is open, or if user is on desktop and the amount of videos is even */
            setCurrent(current === length - 1 ? 0 : current + 1);
            addScript(videos);
        } else if (length === 2 - recordedVideoAmount && width >= 450) {
            /* controls for 3 performers on desktop */
            setCurrent(current === 0 ? current + 1 : current - 1);
            addScript(videos);
        } else if (width < 450) {
            /* handles all other conditions */
            setCurrent(current - recordedVideoAmount === length ? 0 : current + 1);
            addScript(videos);
        } else if (length >= 3) {
            setCurrent(current === length + recordedVideoAmount - 2 ? 0 : current + 1);
            addScript(videos);
        }
    };

    const prevSlide = () => {
        /* same rules apply as for nextSlide, just the opposite */
        if (lightBox && recordedVideoAmount === 1) {
            setCurrent(current === 0 ? length + recordedVideoAmount - 1 : current - 1);
            addScript(videos);
        } else if (lightBox || (width >= 450 && length % 2 == 0)) {
            setCurrent(current === 0 ? length - 1 : current - 1);
            addScript(videos);
        } else if (length === 2 - recordedVideoAmount && width >= 450) {
            setCurrent(current === 1 ? current - 1 : current + 1);
            addScript(videos);
        } else if (width <= 450) {
            setCurrent(current === 0 ? length + recordedVideoAmount : current - 1);
            addScript(videos);
        } else if (length >= 3) {
            setCurrent(current === 0 ? length + recordedVideoAmount - 2 : current - 1);
            addScript(videos);
        }
    };

    const lightBoxToggle = () => {
        /* After light box is toggled, adds script to show video, adds blur and sets threevid condition to false */
        setLightBox(true);
        addScript(videos);
        sendData('blur-video');
        setBlur('blur-video');
        setThreeVid(false);
    };

    const threeVideos = () => {
        /* this handles a condition on lightBox.ts 
      that will add another script if 3 videos exist */
        setThreeVid(true);
    };

    function outSideArrows(ref2: any, ref3: any) {
        /* handles clicking anywhere outside of the chevrons or the lightbox to close it,
      and remove the blur */
        useEffect(() => {
            function handleClickOutside(event: any) {
                if (
                    lightBox === true &&
                    ref2.current &&
                    !ref2.current.contains(event.target) &&
                    ref3.current &&
                    !ref3.current.contains(event.target)
                ) {
                    addScript(videos);
                    setLightBox(false);
                    sendData('');
                    setBlur('');
                }
            }

            document.addEventListener('mousedown', handleClickOutside, { passive: true });
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [lightBox, ref2, ref3]);
    }

    const rightRef = useRef(null);
    const leftRef = useRef(null);
    outSideArrows(rightRef, leftRef);

    return (
        <div className={styles.container}>
            <h2 className={blur != '' ? styles.blur : styles.noBlur}>
                {videoGallery.media_title}
            </h2>
            <div className={styles.slider}>
                {length >= 2 - recordedVideoAmount ? (
                    <div>
                        <svg
                            ref={leftRef}
                            className={`${styles.leftArrow} ${blurStatus === 'blur-gallery' ? styles.blurred : ''}`}
                            onClick={prevSlide}
                            width="30"
                            height="31"
                            viewBox="0 0 30 31"
                            fill="none"
                            xmlns="https://www.w3.org/2000/svg">
                            <rect y="0.5" width="30" height="30" rx="15" fill="#222222" />
                            <path
                                d="M17.8572 8.35717L10.7143 15.5L17.8572 22.6429"
                                stroke="white"
                                strokeWidth="1.42857"
                                strokeMiterlimit="10"
                                strokeLinecap="square"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <svg
                            ref={rightRef}
                            className={`${styles.rightArrow} ${blurStatus === 'blur-gallery' ? styles.blurred : ''}`}
                            onClick={nextSlide}
                            width="30"
                            height="31"
                            viewBox="0 0 30 31"
                            fill="none"
                            xmlns="https://www.w3.org/2000/svg">
                            <rect y="0.5" width="30" height="30" rx="15" fill="#222222" />
                            <path
                                d="M12.1428 8.35717L19.2857 15.5L12.1428 22.6429"
                                stroke="white"
                                strokeWidth="1.42857"
                                strokeMiterlimit="10"
                                strokeLinecap="square"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                ) : null}
                <div className={styles.sliderContainer}>
                    <div className={length >= 1 ? styles.lightBoxes : styles.noLightBoxes}>
                        {performer.is_online === 0 ? (
                            <>
                                <div
                                    role="presentation"
                                    onClick={() => lightBoxToggle()}
                                    className={
                                        lightBox ? styles.noLightBoxes : styles.lightboxContainer1
                                    }></div>
                                <div
                                    role="presentation"
                                    onClick={() => lightBoxToggle()}
                                    className={
                                        lightBox ? styles.noLightBoxes : styles.lightboxContainer2
                                    }></div>
                            </>
                        ) : null}
                        {recordedVideoAmount === 1 ? (
                            <div
                                role="presentation"
                                onClick={() => lightBoxToggle()}
                                className={
                                    lightBox ? styles.noLightBoxes : styles.lightboxContainer7
                                }></div>
                        ) : null}
                        <div
                            role="presentation"
                            onClick={() => lightBoxToggle()}
                            className={
                                lightBox ? styles.noLightBoxes : styles.lightboxContainer3
                            }></div>
                        {length === 1 ? null : (
                            <div
                                role="presentation"
                                className={
                                    lightBox ? styles.noLightBoxes : styles.lightboxContainer4
                                }></div>
                        )}

                        {width >= 450 ? (
                            <div>
                                <div
                                    role="presentation"
                                    onClick={() => {
                                        lightBoxToggle();
                                        threeVideos();
                                    }}
                                    className={
                                        lightBox ? styles.noLightBoxes : styles.lightboxContainer5
                                    }></div>
                                <div
                                    role="presentation"
                                    onClick={() => {
                                        lightBoxToggle();
                                        threeVideos();
                                    }}
                                    className={
                                        lightBox ? styles.noLightBoxes : styles.lightboxContainer6
                                    }></div>
                            </div>
                        ) : null}
                    </div>
                    {index === current && lightBox === false && (
                        <>
                            {performer.videos ? (
                                /* eslint-disable-next-line jsx-a11y/media-has-caption */
                                <video
                                    className={styles.video}
                                    onClick={() => lightBoxToggle()}
                                    controls
                                    poster={`https://s3.amazonaws.com/assets.camrabbit.sex/${thumbnailPath}`}>
                                    <source
                                        src={`https://s3.amazonaws.com/assets.camrabbit.sex/${performer.videos[0].path}`}
                                        type="video/mp4"
                                    />
                                </video>
                            ) : (
                                <div
                                    className={length >= 1 ? styles.video : styles.noVideo}
                                    data-awe-container-id="video-player-0"></div>
                            )}
                        </>
                    )}
                    {(index === current && width >= 450 && lightBox === false) ||
                    (index + 1 === current && width < 450 && lightBox === false) ||
                    (length + recordedVideoAmount <= 1 && width < 450 && lightBox === false) ? (
                        <div className={styles.imgContainer}>
                            <Image
                                loader={() => src}
                                src={src}
                                alt="performer"
                                className={
                                    current === 0 || current === 1
                                        ? styles.performerCta
                                        : styles.null
                                }
                                key={index}
                                height={311}
                                width={500}
                            />
                        </div>
                    ) : null}
                    {index + 1 === current && width >= 450 && lightBox === false ? (
                        <div className={styles.threeVideo}>
                            <div
                                className={
                                    length >= 2 - recordedVideoAmount
                                        ? styles.video
                                        : styles.noVideo
                                }
                                data-awe-container-id={`video-player-${
                                    recordedVideoAmount ? 0 : 1
                                }`}></div>
                            <div
                                className={
                                    length >= 3 - recordedVideoAmount && width >= 450
                                        ? styles.video
                                        : styles.noVideo
                                }
                                data-awe-container-id={`video-player-${
                                    recordedVideoAmount ? 1 : 2
                                }`}></div>
                        </div>
                    ) : null}
                    {index + 2 === current &&
                    lightBox === false &&
                    length === 2 + recordedVideoAmount ? (
                        <div
                            className={styles.video}
                            data-awe-container-id={`video-player-${
                                recordedVideoAmount ? 2 : 3
                            }`}></div>
                    ) : null}
                    {(current === 0 && width >= 450 && lightBox === false) ||
                    (current === 0 && length + recordedVideoAmount <= 1 && lightBox === false) ||
                    (current === 1 && width <= 450 && lightBox === false) ? (
                        <div
                            className={
                                length + recordedVideoAmount === 0
                                    ? `${styles.ctaContent} ${styles.ctaContentNoVideo}`
                                    : length + recordedVideoAmount <= 1
                                    ? styles.ctaContent
                                    : `${styles.ctaContent} ${styles.ctaContentManyVideos}`
                            }>
                            <h3>{videoGallery.cta_title}</h3>
                            <svg
                                width="44"
                                height="58"
                                viewBox="0 0 44 58"
                                fill="none"
                                xmlns="https://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M35.5416 19.1666H38.25C41.2291 19.1666 43.6666 21.6041 43.6666 24.5833V51.6666C43.6666 54.6458 41.2291 57.0833 38.25 57.0833H5.74998C2.77081 57.0833 0.333313 54.6458 0.333313 51.6666V24.5833C0.333313 21.6041 2.77081 19.1666 5.74998 19.1666H8.45831V13.75C8.45831 6.27498 14.525 0.208313 22 0.208313C29.475 0.208313 35.5416 6.27498 35.5416 13.75V19.1666ZM13.6041 13.75V19.1666H30.3958V13.75C30.3958 9.11873 26.6312 5.35415 22 5.35415C17.3687 5.35415 13.6041 9.11873 13.6041 13.75ZM20.6805 45.7573L22 46.9585L23.3195 45.7664L23.3554 45.7338C28.0218 41.4933 31.1 38.696 31.1 35.265C31.1 32.4622 28.8978 30.26 26.095 30.26C24.5116 30.26 22.9919 30.9971 22 32.1619C21.0081 30.9971 19.4884 30.26 17.905 30.26C15.1022 30.26 12.9 32.4622 12.9 35.265C12.9 38.702 15.9889 41.503 20.669 45.7469L20.6805 45.7573Z"
                                    fill="#222222"
                                />
                            </svg>
                            <a
                                id="videoCta"
                                className={buttonStyles.primaryButton}
                                data-event-category="Outbound Click"
                                data-event-action="CTA Interstitial Videos"
                                data-event-label="Button-CTA"
                                href={`https://rabbits.webcam/?id=2333&product_id=36&tag=signup&passthru1=${gender}&passthru2=${currentLang}&exid={exid}&tfid={tfid}`}>
                                <span>{videoGallery.cta_button}</span>
                            </a>
                        </div>
                    ) : null}
                    <LightBox
                        videos={videos}
                        current={current}
                        lightBox={lightBox}
                        sendLightBox={getLightBox}
                        sendBlur={getBlur}
                        threeVideos={threeVid}
                        performer={performer}
                        recordedVideoAmount={recordedVideoAmount}
                        thumbnailPath={thumbnailPath}
                    />
                </div>
            </div>
        </div>
    );
}
